import { useEffect, useState, useCallback, useRef } from 'react'
import axios from '../../lib/axios'
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  Check,
  Lightbulb,
  Mail,
  Globe,
  Server,
  Lock,
  FileText,
  Activity,
  TrendingDown,
  TrendingUp,
  AlertOctagon,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import './DNSHealth.css'
import '../Pages.css'

const RECORD_CONFIG = {
  spf: { label: 'SPF', icon: FileText, desc: 'Sender Policy Framework' },
  dkim: { label: 'DKIM', icon: Lock, desc: 'DomainKeys Identified Mail' },
  dmarc: { label: 'DMARC', icon: Shield, desc: 'Domain-based Message Authentication' },
  mx: { label: 'MX', icon: Server, desc: 'Mail Exchange Records' },
  ptr: { label: 'PTR', icon: Globe, desc: 'Pointer Record (Reverse DNS)' }
}

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' },
  high: { label: 'High', bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316' },
  warning: { label: 'Warning', bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308' },
  medium: { label: 'Medium', bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }
}

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', icon: AlertOctagon },
  warning: { label: 'Warning', bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308', icon: AlertTriangle }
}

function getScoreColor(score) {
  if (score >= 90) return '#22c55e'
  if (score >= 80) return '#3b82f6'
  if (score >= 70) return '#eab308'
  if (score >= 60) return '#f97316'
  return '#ef4444'
}

function getGradeLabel(grade) {
  const labels = { A: 'Excellent', B: 'Good', C: 'Fair', D: 'Poor', F: 'Critical' }
  return labels[grade] || ''
}

function getRateColor(rate, type) {
  if (type === 'bounce') {
    if (rate <= 2) return '#22c55e'
    if (rate <= 5) return '#eab308'
    return '#ef4444'
  }
  // open/delivery rate
  if (rate >= 30) return '#22c55e'
  if (rate >= 15) return '#eab308'
  return '#ef4444'
}

// Simple sparkline bar chart
function MiniBarChart({ data, dataKey, color, height = 40 }) {
  if (!data || data.length === 0) return null
  const max = Math.max(...data.map(d => d[dataKey] || 0), 1)
  const barW = Math.max(2, Math.min(8, Math.floor(200 / data.length) - 1))

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height }}>
      {data.map((d, i) => (
        <div
          key={i}
          title={`${d.date}: ${d[dataKey]}`}
          style={{
            width: barW,
            height: Math.max(1, (d[dataKey] / max) * height),
            background: color,
            borderRadius: '1px',
            opacity: 0.8,
          }}
        />
      ))}
    </div>
  )
}

export default function DNSHealth() {
  const [activeTab, setActiveTab] = useState('dns')
  const [accounts, setAccounts] = useState([])
  const [selectedEmail, setSelectedEmail] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [inputMode, setInputMode] = useState('account')
  const [loading, setLoading] = useState(false)
  const [accountsLoading, setAccountsLoading] = useState(true)
  const [results, setResults] = useState(null)
  const [error, setError] = useState(null)
  const [copiedAction, setCopiedAction] = useState(null)
  const [accountStatuses, setAccountStatuses] = useState({})
  const [checkingAccounts, setCheckingAccounts] = useState({})
  const quickChecksRan = useRef(false)

  // Domain Monitoring state
  const [monitorLoading, setMonitorLoading] = useState(false)
  const [blacklistData, setBlacklistData] = useState(null)
  const [trendData, setTrendData] = useState(null)
  const [domainStats, setDomainStats] = useState(null)
  const [spamSignals, setSpamSignals] = useState(null)
  const [monitorDays, setMonitorDays] = useState(30)
  const [monitorError, setMonitorError] = useState(null)

  useEffect(() => {
    axios.get('email/accounts')
      .then(res => {
        const data = res.data
        const list = data?.accounts || (Array.isArray(data) ? data : data.data || [])
        setAccounts(list)
        if (list.length > 0) {
          setSelectedEmail(list[0].email || list[0].smtp_email || '')
        }
      })
      .catch(() => setAccounts([]))
      .finally(() => setAccountsLoading(false))
  }, [])

  const getEmailToCheck = useCallback(() => {
    return inputMode === 'manual' ? manualEmail.trim() : selectedEmail
  }, [inputMode, manualEmail, selectedEmail])

  const runFullCheck = useCallback(async (emailOverride) => {
    const email = emailOverride || getEmailToCheck()
    if (!email) return

    setLoading(true)
    setError(null)
    setResults(null)

    try {
      const [checkRes, scoreRes] = await Promise.all([
        axios.post('dns/check', { email }),
        axios.get(`dns/score/${encodeURIComponent(email)}`)
      ])

      setResults({
        ...checkRes.data,
        scoreData: scoreRes.data
      })
    } catch (err) {
      setError(err.response?.data?.detail || 'DNS check failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [getEmailToCheck])

  const runQuickCheck = useCallback(async (email) => {
    setCheckingAccounts(prev => ({ ...prev, [email]: true }))
    try {
      const res = await axios.get(`dns/quick-check/${encodeURIComponent(email)}`)
      setAccountStatuses(prev => ({ ...prev, [email]: res.data }))
    } catch {
      setAccountStatuses(prev => ({ ...prev, [email]: null }))
    } finally {
      setCheckingAccounts(prev => ({ ...prev, [email]: false }))
    }
  }, [])

  const runAllQuickChecks = useCallback(() => {
    accounts.forEach(acc => {
      const email = acc.email || acc.smtp_email
      if (email) runQuickCheck(email)
    })
  }, [accounts, runQuickCheck])

  useEffect(() => {
    if (accounts.length > 0 && !quickChecksRan.current) {
      quickChecksRan.current = true
      runAllQuickChecks()
    }
  }, [accounts]) // eslint-disable-line react-hooks/exhaustive-deps

  // Domain Monitoring fetch
  const fetchMonitoringData = useCallback(async () => {
    const email = getEmailToCheck()
    setMonitorLoading(true)
    setMonitorError(null)

    const promises = [
      axios.get(`dns/reputation-trends?days=${monitorDays}`).catch(() => null),
      axios.get(`dns/domain-stats?days=${monitorDays}`).catch(() => null),
      axios.get('dns/spam-signals').catch(() => null),
    ]

    if (email) {
      promises.push(axios.get(`dns/blacklist-check/${encodeURIComponent(email)}`).catch(() => null))
    }

    try {
      const [trendsRes, domainsRes, signalsRes, blRes] = await Promise.all(promises)
      setTrendData(trendsRes?.data || null)
      setDomainStats(domainsRes?.data || null)
      setSpamSignals(signalsRes?.data || null)
      if (blRes) setBlacklistData(blRes.data)
    } catch (err) {
      setMonitorError('Failed to load monitoring data.')
    } finally {
      setMonitorLoading(false)
    }
  }, [getEmailToCheck, monitorDays])

  useEffect(() => {
    if (activeTab === 'monitor') {
      fetchMonitoringData()
    }
  }, [activeTab, monitorDays]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyAction = (text, idx) => {
    navigator.clipboard.writeText(text)
    setCopiedAction(idx)
    setTimeout(() => setCopiedAction(null), 2000)
  }

  const handleAccountClick = (email) => {
    setInputMode('account')
    setSelectedEmail(email)
    runFullCheck(email)
  }

  const score = results?.scoreData
  const scorePercent = score?.percentage || 0
  const scoreGrade = score?.grade || '-'
  const scoreColor = getScoreColor(scorePercent)

  const radius = 56
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (scorePercent / 100) * circumference

  const TABS = [
    { id: 'dns', label: 'DNS Records', icon: Shield },
    { id: 'monitor', label: 'Domain Monitoring', icon: Activity },
  ]

  return (
    <div className="page">
      {/* Hero */}
      <div className="dns-health-hero">
        <h2>DNS & Domain Health</h2>
        <p>Verify authentication records, monitor reputation, and detect deliverability issues</p>

        <div className="dns-check-form">
          <div className="dns-form-group">
            <label>Check Method</label>
            <select value={inputMode} onChange={e => setInputMode(e.target.value)}>
              <option value="account">Connected Account</option>
              <option value="manual">Manual Email</option>
            </select>
          </div>

          {inputMode === 'account' ? (
            <div className="dns-form-group" style={{ flex: 2 }}>
              <label>Email Account</label>
              <select
                value={selectedEmail}
                onChange={e => setSelectedEmail(e.target.value)}
                disabled={accountsLoading || accounts.length === 0}
              >
                {accountsLoading && <option>Loading accounts...</option>}
                {!accountsLoading && accounts.length === 0 && <option>No accounts connected</option>}
                {accounts.map(acc => {
                  const email = acc.email || acc.smtp_email
                  return <option key={acc._id || acc.id || email} value={email}>{email}</option>
                })}
              </select>
            </div>
          ) : (
            <div className="dns-form-group" style={{ flex: 2 }}>
              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@yourdomain.com"
                value={manualEmail}
                onChange={e => setManualEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (activeTab === 'dns' ? runFullCheck() : fetchMonitoringData())}
              />
            </div>
          )}

          <button
            className="dns-check-btn"
            onClick={() => activeTab === 'dns' ? runFullCheck() : fetchMonitoringData()}
            disabled={(activeTab === 'dns' ? loading : monitorLoading) || (!getEmailToCheck() && activeTab === 'dns')}
          >
            {(activeTab === 'dns' ? loading : monitorLoading)
              ? <RefreshCw size={16} style={{ animation: 'dns-spin 0.8s linear infinite' }} />
              : <Search size={16} />
            }
            {(activeTab === 'dns' ? loading : monitorLoading) ? 'Checking...' : activeTab === 'dns' ? 'Check Health' : 'Run Monitoring'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="dns-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              className={`dns-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* ════════════════ DNS RECORDS TAB ════════════════ */}
      {activeTab === 'dns' && (
        <>
          {error && (
            <div style={{
              padding: '1rem 1.25rem', marginBottom: '1.5rem', borderRadius: 'var(--radius-lg)',
              background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)',
              color: '#ef4444', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.625rem'
            }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {loading && (
            <div className="dns-loading">
              <div className="dns-loading-spinner" />
              <p>Running DNS checks for {getEmailToCheck()}...</p>
            </div>
          )}

          {results && !loading && (
            <>
              {/* Score Section */}
              <div className="dns-score-section">
                <div className="dns-score-gauge">
                  <svg viewBox="0 0 128 128">
                    <circle className="score-bg" cx="64" cy="64" r={radius} />
                    <circle
                      className="score-fill"
                      cx="64" cy="64" r={radius}
                      stroke={scoreColor}
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                    />
                  </svg>
                  <div className="dns-score-center">
                    <div className="dns-score-number" style={{ color: scoreColor }}>{scorePercent}</div>
                    <div className="dns-score-grade" style={{ color: scoreColor }}>Grade {scoreGrade}</div>
                  </div>
                </div>

                <div className="dns-score-details">
                  <h3>
                    {getGradeLabel(scoreGrade)} Deliverability
                    {results.scoreData?.details && (
                      <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--color-text-tertiary)', marginLeft: '0.5rem' }}>
                        {results.domain}
                      </span>
                    )}
                  </h3>
                  <p>
                    {scorePercent >= 90 && 'Your domain authentication is well configured. Emails should land in inbox.'}
                    {scorePercent >= 70 && scorePercent < 90 && 'Some DNS records need attention. Fix issues below to improve deliverability.'}
                    {scorePercent < 70 && 'Significant DNS issues detected. Fix critical records to avoid spam folders.'}
                  </p>

                  <div className="dns-score-breakdown">
                    {['spf', 'dkim', 'dmarc', 'mx', 'ptr'].map(key => {
                      const record = results[key]
                      if (!record && key === 'ptr') return null
                      const exists = record?.exists
                      return (
                        <span key={key} className="dns-score-tag" style={{
                          background: exists ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                          color: exists ? '#22c55e' : '#ef4444'
                        }}>
                          {exists ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {key.toUpperCase()}
                        </span>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* DNS Records Grid */}
              <div className="dns-records-grid">
                {['spf', 'dkim', 'dmarc', 'mx', 'ptr'].map(key => {
                  const record = results[key]
                  if (!record && key === 'ptr') return null
                  const config = RECORD_CONFIG[key]
                  const Icon = config.icon
                  const exists = record?.exists

                  return (
                    <div key={key} className="dns-record-card">
                      <div className="dns-record-header">
                        <div className="dns-record-title">
                          <div className="dns-record-icon" style={{
                            background: exists ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                            color: exists ? '#22c55e' : '#ef4444'
                          }}>
                            <Icon size={16} />
                          </div>
                          <div>
                            <h4>{config.label}</h4>
                            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)' }}>{config.desc}</span>
                          </div>
                        </div>
                        {exists
                          ? <CheckCircle size={18} color="#22c55e" />
                          : <XCircle size={18} color="#ef4444" />
                        }
                      </div>

                      <div className="dns-record-body">
                        {record?.value ? (
                          <div className="dns-record-value">{record.value}</div>
                        ) : (
                          <p className="dns-record-none">
                            {exists ? 'Record found (no value returned)' : 'No record found'}
                          </p>
                        )}

                        {record?.issues && record.issues.length > 0 && (
                          <div className="dns-record-issues">
                            {record.issues.map((issue, i) => (
                              <div key={i} className="dns-record-issue">
                                <AlertTriangle size={12} />
                                <span>{issue}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {record?.error && (
                          <div className="dns-record-issue">
                            <AlertTriangle size={12} />
                            <span>{record.error}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Recommendations */}
              {results.recommendations && results.recommendations.length > 0 && (
                <div className="dns-recommendations">
                  <div className="dns-recommendations-header">
                    <Lightbulb size={18} color="#6366f1" />
                    <h3>Recommendations ({results.recommendations.length})</h3>
                  </div>
                  <ul className="dns-rec-list">
                    {results.recommendations.map((rec, i) => {
                      const pConfig = PRIORITY_CONFIG[rec.priority] || PRIORITY_CONFIG.medium
                      return (
                        <li key={i} className="dns-rec-item">
                          <span className="dns-rec-badge" style={{ background: pConfig.bg, color: pConfig.color }}>
                            {pConfig.label}
                          </span>
                          <div className="dns-rec-content">
                            <h4>{rec.title}</h4>
                            <p>{rec.description}</p>
                            {rec.action && (
                              <div className="dns-rec-action">
                                <span style={{ flex: 1 }}>{rec.action}</span>
                                <button onClick={() => handleCopyAction(rec.action, i)} title="Copy">
                                  {copiedAction === i ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                              </div>
                            )}
                            {rec.impact && <div className="dns-rec-impact">Impact: {rec.impact}</div>}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Account Quick Status */}
          {accounts.length > 0 && (
            <div className="dns-accounts-section">
              <div className="dns-accounts-header">
                <h3>Account Quick Status</h3>
                <button className="btn btn-ghost" onClick={runAllQuickChecks} style={{ fontSize: '0.8rem', padding: '0.4rem 0.875rem' }}>
                  <RefreshCw size={14} /> Refresh All
                </button>
              </div>

              <div className="dns-accounts-grid">
                {accounts.map(acc => {
                  const email = acc.email || acc.smtp_email
                  if (!email) return null
                  const status = accountStatuses[email]
                  const isChecking = checkingAccounts[email]
                  const domain = email.split('@')[1]

                  return (
                    <div
                      key={acc._id || acc.id || email}
                      className={`dns-account-card ${isChecking ? 'checking' : ''}`}
                      onClick={() => handleAccountClick(email)}
                    >
                      <div className="dns-account-avatar">
                        <Mail size={16} />
                      </div>
                      <div className="dns-account-info">
                        <div className="email">{email}</div>
                        <div className="domain">{domain}</div>
                      </div>
                      <div className="dns-account-status">
                        {isChecking ? (
                          <RefreshCw size={14} style={{ animation: 'dns-spin 0.8s linear infinite', color: 'var(--color-text-tertiary)' }} />
                        ) : status?.checks ? (
                          <>
                            {['spf', 'dkim', 'dmarc', 'ptr'].map(key => (
                              <div
                                key={key}
                                className="dns-account-dot"
                                title={`${key.toUpperCase()}: ${status.checks[key] ? 'Pass' : 'Fail'}`}
                                style={{ background: status.checks[key] ? '#22c55e' : '#ef4444' }}
                              />
                            ))}
                          </>
                        ) : (
                          <span style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)' }}>Click to check</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {!accountsLoading && accounts.length === 0 && !results && !loading && (
            <div className="dns-empty-state">
              <div className="dns-empty-icon"><Shield size={28} /></div>
              <h3>Check Your Domain Health</h3>
              <p>Enter an email address above to verify SPF, DKIM, DMARC, and other DNS records that affect your email deliverability.</p>
            </div>
          )}
        </>
      )}

      {/* ════════════════ DOMAIN MONITORING TAB ════════════════ */}
      {activeTab === 'monitor' && (
        <>
          {/* Date range selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', fontWeight: 600 }}>Period:</span>
            {[7, 30, 60, 90].map(d => (
              <button
                key={d}
                className={`btn ${monitorDays === d ? 'btn-primary' : 'btn-ghost'}`}
                style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
                onClick={() => setMonitorDays(d)}
              >
                {d}d
              </button>
            ))}
          </div>

          {monitorError && (
            <div style={{
              padding: '1rem 1.25rem', marginBottom: '1.5rem', borderRadius: 'var(--radius-lg)',
              background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)',
              color: '#ef4444', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.625rem'
            }}>
              <AlertTriangle size={16} />
              {monitorError}
            </div>
          )}

          {monitorLoading && (
            <div className="dns-loading">
              <div className="dns-loading-spinner" />
              <p>Loading domain monitoring data...</p>
            </div>
          )}

          {!monitorLoading && (
            <>
              {/* Spam Signals / Alerts */}
              {spamSignals && (
                <div className="dns-monitor-section">
                  <div className="dns-monitor-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <AlertOctagon size={18} color={
                        spamSignals.risk_level === 'critical' ? '#ef4444'
                          : spamSignals.risk_level === 'warning' ? '#eab308' : '#22c55e'
                      } />
                      <h3>Spam Signals</h3>
                    </div>
                    <span className="dns-risk-badge" style={{
                      background: spamSignals.risk_level === 'critical' ? 'rgba(239, 68, 68, 0.1)'
                        : spamSignals.risk_level === 'warning' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                      color: spamSignals.risk_level === 'critical' ? '#ef4444'
                        : spamSignals.risk_level === 'warning' ? '#eab308' : '#22c55e'
                    }}>
                      {spamSignals.risk_level === 'healthy' ? 'All Clear' : spamSignals.risk_level.charAt(0).toUpperCase() + spamSignals.risk_level.slice(1)}
                    </span>
                  </div>

                  {spamSignals.signals.length === 0 ? (
                    <div style={{ padding: '1.25rem 1.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle size={16} color="#22c55e" />
                      No spam signals detected. Your sending reputation looks healthy.
                    </div>
                  ) : (
                    <div className="dns-signal-list">
                      {spamSignals.signals.map((sig, i) => {
                        const sConfig = SEVERITY_CONFIG[sig.severity] || SEVERITY_CONFIG.warning
                        const SigIcon = sConfig.icon
                        return (
                          <div key={i} className="dns-signal-item">
                            <div className="dns-signal-icon" style={{ background: sConfig.bg, color: sConfig.color }}>
                              <SigIcon size={16} />
                            </div>
                            <div className="dns-signal-content">
                              <div className="dns-signal-meta">
                                <span className="dns-rec-badge" style={{ background: sConfig.bg, color: sConfig.color }}>
                                  {sConfig.label}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>{sig.account}</span>
                              </div>
                              <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-primary)', margin: '0.25rem 0' }}>
                                {sig.message}
                              </p>
                              <p style={{ fontSize: '0.775rem', color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
                                {sig.detail}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Blacklist Check */}
              {blacklistData && (
                <div className="dns-monitor-section">
                  <div className="dns-monitor-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <ShieldCheck size={18} color={blacklistData.clean ? '#22c55e' : '#ef4444'} />
                      <h3>Blacklist Check</h3>
                    </div>
                    <span className="dns-risk-badge" style={{
                      background: blacklistData.clean ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: blacklistData.clean ? '#22c55e' : '#ef4444'
                    }}>
                      {blacklistData.clean ? 'Clean' : `Listed on ${blacklistData.total_listed}`}
                    </span>
                  </div>

                  <div style={{ padding: '0.75rem 1.5rem', fontSize: '0.775rem', color: 'var(--color-text-tertiary)' }}>
                    Checked IP: {blacklistData.ip || 'N/A'} &middot; Domain: {blacklistData.domain} &middot; {blacklistData.total_checked} lists checked
                  </div>

                  <div className="dns-bl-grid">
                    {blacklistData.blacklists.map((bl, i) => (
                      <div key={i} className="dns-bl-item">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                          {bl.listed
                            ? <XCircle size={14} color="#ef4444" />
                            : bl.error
                              ? <AlertTriangle size={14} color="#eab308" />
                              : <CheckCircle size={14} color="#22c55e" />
                          }
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>{bl.name}</span>
                        </div>
                        <span style={{
                          fontSize: '0.7rem', fontWeight: 600,
                          color: bl.listed ? '#ef4444' : bl.error ? '#eab308' : '#22c55e'
                        }}>
                          {bl.listed ? 'LISTED' : bl.error === 'timeout' ? 'TIMEOUT' : bl.error ? 'ERROR' : 'CLEAN'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reputation Trends */}
              {trendData && Object.keys(trendData.accounts || {}).length > 0 && (
                <div className="dns-monitor-section">
                  <div className="dns-monitor-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <TrendingUp size={18} color="#6366f1" />
                      <h3>Reputation Trends</h3>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>Last {trendData.days} days</span>
                  </div>

                  <div className="dns-trends-grid">
                    {Object.entries(trendData.accounts).map(([email, days]) => {
                      const totalSent = days.reduce((s, d) => s + d.sent, 0)
                      const totalBounced = days.reduce((s, d) => s + d.bounced, 0)
                      const totalOpens = days.reduce((s, d) => s + d.opens, 0)
                      const totalDelivered = totalSent - totalBounced
                      const avgBounce = totalSent ? (totalBounced / totalSent * 100).toFixed(1) : 0
                      const avgOpen = totalDelivered ? (totalOpens / totalDelivered * 100).toFixed(1) : 0

                      return (
                        <div key={email} className="dns-trend-card">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <div>
                              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>{email}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)' }}>{totalSent} sent &middot; {totalDelivered} delivered</div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Bounce Rate</div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: getRateColor(avgBounce, 'bounce') }}>{avgBounce}%</div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Open Rate</div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: getRateColor(avgOpen, 'open') }}>{avgOpen}%</div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.6rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Daily sends</div>
                              <MiniBarChart data={days} dataKey="sent" color="#6366f1" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.6rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Daily bounces</div>
                              <MiniBarChart data={days} dataKey="bounced" color="#ef4444" />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Domain-wise Stats */}
              {domainStats && domainStats.domains && domainStats.domains.length > 0 && (
                <div className="dns-monitor-section">
                  <div className="dns-monitor-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <BarChart3 size={18} color="#6366f1" />
                      <h3>Deliverability by Provider</h3>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>Top {domainStats.domains.length} recipient domains</span>
                  </div>

                  <div className="dns-domain-table">
                    <div className="dns-domain-row dns-domain-row-header">
                      <span>Domain</span>
                      <span>Sent</span>
                      <span>Delivered</span>
                      <span>Bounced</span>
                      <span>Bounce Rate</span>
                      <span>Open Rate</span>
                      <span>Click Rate</span>
                    </div>
                    {domainStats.domains.map((d, i) => (
                      <div key={i} className="dns-domain-row">
                        <span style={{ fontWeight: 600 }}>{d.domain}</span>
                        <span>{d.sent}</span>
                        <span>{d.delivered}</span>
                        <span style={{ color: d.bounced > 0 ? '#ef4444' : 'inherit' }}>{d.bounced}</span>
                        <span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            color: getRateColor(d.bounce_rate, 'bounce')
                          }}>
                            {d.bounce_rate > 5 ? <ArrowUpRight size={12} /> : null}
                            {d.bounce_rate}%
                          </span>
                        </span>
                        <span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            color: getRateColor(d.open_rate, 'open')
                          }}>
                            {d.open_rate < 10 ? <ArrowDownRight size={12} /> : null}
                            {d.open_rate}%
                          </span>
                        </span>
                        <span>{d.click_rate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty monitoring state */}
              {!spamSignals && !blacklistData && !trendData && !domainStats && (
                <div className="dns-empty-state">
                  <div className="dns-empty-icon"><Activity size={28} /></div>
                  <h3>Domain Monitoring</h3>
                  <p>Click "Run Monitoring" to check blacklists, analyze reputation trends, and detect spam signals across your accounts.</p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
